import { Check } from 'lucide-react';
import React from 'react';
import { hexToRgba } from '../hooks/useCustomerInfo';

interface Step {
  name: string;
  step: number;
  subtitle?: string;
  icon: React.ReactNode;
}

interface ColorScheme {
  activeStep?: string;
  completedStep?: string;
  inactiveStep?: string;
  activeText?: string;
  completedText?: string;
  inactiveText?: string;
  completedIcon?: string;
  progressBar?: string;
  stepBackground?: string;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (step: number) => void;
  colors?: ColorScheme;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  onStepClick,
  colors = {}
}) => {
  // Default colors
 const defaultColors: ColorScheme = {
  activeStep: "#DDB889",
  completedStep: "#22c55e",  // ✨ Green for completed!
  inactiveStep: "#DDB889/20",
  activeText: "#DDB889",
  completedText: "#22c55e",  // ✨ Green text for completed!
  inactiveText: "#DDB889/40",
  completedIcon: "#FFFFFF",  // White checkmark on green
  progressBar: "#22c55e",  // ✨ Green progress bar!
  stepBackground: "#000"
};

  // Merge provided colors with defaults
  const theme = {
    activeStep: colors.activeStep || defaultColors.activeStep,
    completedStep: colors.completedStep || defaultColors.completedStep,
    inactiveStep: colors.inactiveStep || defaultColors.inactiveStep,
    activeText: colors.activeText || defaultColors.activeText,
    completedText: colors.completedText || defaultColors.completedText,
    inactiveText: colors.inactiveText || defaultColors.inactiveText,
    completedIcon: colors.completedIcon || defaultColors.completedIcon,
    progressBar: colors.progressBar || defaultColors.progressBar,
    stepBackground: colors.stepBackground || defaultColors.stepBackground
  };

  return (
    <div className="lp-relative lp-overflow-hidden" style={{ backgroundColor: theme.stepBackground }}>

      {/* Main content */}
      <div className="lp-relative lp-z-10 lp-p-8 lp-backdrop-blur-sm lp-border lp-border-[#DDB889]/10 lp-rounded-2xl
        lp-shadow-[0_0_15px_rgba(221,184,137,0.07)] hover:lp-shadow-[0_0_20px_rgba(221,184,137,0.1)]
        lp-transition-all lp-duration-700">
        <div className="lp-flex lp-items-center lp-justify-between">
          {steps.map((step, index) => {
            const isCompleted = currentStep > step.step;
            const isCurrent = currentStep === step.step;

            return (
              <React.Fragment key={step.name}>
                <div
                  className="lp-relative lp-flex lp-flex-col lp-items-center lp-group"
                  onClick={() => onStepClick && onStepClick(step.step)}
                >
                  {/* Step circle with dynamic styling */}
                  <div
  className={`
    lp-w-12 lp-h-12 lp-rounded-full lp-flex lp-items-center lp-justify-center
    lp-transition-all lp-duration-500 lp-ease-in-out
    ${onStepClick ? 'lp-cursor-pointer hover:lp-scale-105' : ''}
  `}
  style={{
    backgroundColor: isCompleted
      ? theme.completedStep
      : 'black',
    borderWidth: isCompleted
      ? '0px'
      : isCurrent ? '2px' : '1px',
    borderColor: isCurrent
      ? theme.activeStep || "#DDB889"
      : hexToRgba(theme.inactiveStep || "#DDB889/20"),
    color: isCompleted
      ? theme.completedIcon
      : isCurrent ? theme.activeText || "#DDB889" : hexToRgba(theme.inactiveText || "#DDB889/40"),
    boxShadow: isCompleted
      ? '0 0 15px rgba(34,197,94,0.3)'  // ✨ Green glow for completed!
      : isCurrent ? '0 0 15px rgba(221,184,137,0.1)' : 'none'
  }}
>
                    {isCompleted ? (
                      <Check size={24} style={{ color: theme.completedIcon }} />
                    ) : (
                      React.cloneElement(step.icon as React.ReactElement, {
                        size: 24,
                        className: `lp-transition-transform lp-duration-300 ${
                          isCurrent ? 'group-hover:lp-scale-110' : ''
                        }`,
                        style: {
                          color: isCurrent
                            ? theme.activeText || "#DDB889"
                            : hexToRgba(theme.inactiveText || "#DDB889/40") // Convert inactiveText here too with null check
                        }
                      })
                    )}
                  </div>

                  {/* Step text with dynamic styling */}
                  <div className="lp-text-sm lp-font-medium lp-text-center lp-mt-3 lp-transition-all lp-duration-300"
                    style={{
                      color: isCompleted
                        ? theme.completedText || "#DDB889"
                        : isCurrent ? theme.activeText || "#DDB889" : hexToRgba(theme.inactiveText || "#DDB889/40") // Convert inactiveText with null check
                    }}>
                    {step.name}
                    {step.subtitle && (
                      <span className="lp-block lp-text-xs lp-mt-1 lp-opacity-70 lp-transition-opacity lp-duration-300">
                        {step.subtitle}
                      </span>
                    )}
                  </div>
                </div>

                {/* Connection line with dynamic styling */}
                {index < steps.length - 1 && (
                  <div className="lp-flex-1 lp-h-0.5 lp-mx-4 lp-bg-[#DDB889]/10 lp-self-start lp-mt-6 lp-rounded-full">
                    <div
                      className="lp-h-full lp-rounded-full lp-transition-all lp-duration-700 lp-ease-in-out"
                      style={{
                        width: isCompleted ? '100%' : '0%',
                        backgroundColor: theme.progressBar,
                        boxShadow: isCompleted ? '0 0 10px rgba(221,184,137,0.2)' : 'none'
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepIndicator;
